import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import {
  Grid,
  Box,
  Button,
  Typography,
  Paper,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tab,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import { SVGRenderer, CanvasRenderer } from "echarts/renderers";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
//amplify
import { API } from "aws-amplify";

//contexts
import { UserContext } from "../contexts";
//drag and drop components
import EntitiesBar from "../components/querybuilder/EntitiesBar";
import SimpleFilters from "../components/querybuilder/simplemode/SimpleFilters";

//react router
import { useNavigate, useParams, useLocation } from "react-router-dom";

import { createRules, evaluateField, normalizeOperator } from "../methods";
import QueryInfo from "../components/querybuilder/QueryInfo";
import useUndoRedo from "../hooks/useUndoRedo";

import {
  GetAllFieldsByTable,
  GetAllTables,
  GetPatientCount,
  GetEncounterCount,
  GetMedicationCount,
  GetProcedureCount,
  GetLabCount,
  GetTopTenDiagnoses,
  GetTopTenMedications,
  GetTopTenProcedures,
  GetAgeDistribution,
  GetAdiScoreDistribution,
  GetGenderDistribution,
  GetPatientHeatmapByState,
  QueryPatientData
} from "../services";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import DeleteIcon from "@mui/icons-material/Delete";
import ExecuteQueryModal from "../components/ExecuteQueryModal";
import {
  getSharedQueries,
  getUsersQueries,
  checkDownloads,
  deleteMySQLQuery,
  deleteS3File,
} from "../services";
import AskAIModal from "../components/querybuilder/AskAIModal";
import { useDispatch, useSelector } from "react-redux";
import QueryResults from "../components/querybuilder/components/QueryResults";
import Counts from "../components/querybuilder/Counts";
import {
  FirstPage,
  LastPage,
} from "@mui/icons-material";
import { formatQuery } from "react-querybuilder";
import DataObjectIcon from '@mui/icons-material/DataObject';
import CloseIcon from '@mui/icons-material/Close';
import StepperButtons from "../components/querybuilder/StepperButtons";
import walden from "../themes/echarts/Walden";
import { applyEChartsTheme } from "../themes/echartsTheme";
import ChartDataAccordion from "../components/querybuilder/ChartDataAccordion";
import ListDataAccordion from "../components/querybuilder/ListDataAccordion";
import { v4 as uuidv4 } from 'uuid';
import CryptoJS from "crypto-js";
import CustomSnackbar from "../components/Snackbar";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import FlowLogic from "../components/querybuilder/FlowLogic";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { clearDatabaseSelection, selectDatabase, selectMetadata, setSelectedDatabase, setSelectedMetadata, selectQueryOptions } from "../features/settings/settingsSlice";
import { selectAIFilters } from "../features/chatbot/chatbotSlice";
import { setSelectedAsset } from "../features/userAssets/userAssetsSlice";
import { getCategoryColor } from "../utilities/ColorGenerator";
import TopToolbar from "../components/querybuilder/TopToolbar";
import AnalysisModal from "../components/analysis/AnalysisModal";
import { Folder, Wrench, BarChart3 } from "lucide-react";

const OPTIONAL_COUNT_KEYS = ["encounters", "medications", "procedures", "labs"];

const QueryBuilder = () => {
  //contexts
  const {
    userAppSettings,
    setUserAppSettings,
    myQueries,
    setMyQueries,
    sharedQueries,
    setSharedQueries,
    activeStep,
    setActiveStep,
  } = useContext(UserContext);
  //destructuring saved queries from context
  const { savedQueries } = userAppSettings;
  //state
  const [entities, setEntities] = useState([]);
  const [fields, setFields] = useState({});
  const [displayFields, setDisplayFields] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState({});
  const [loading, setLoading] = useState(false);
  const [downloads, setDownloads] = useState([]);
  const [snackbarInfo, setSnackbarInfo] = useState({ open: false, severity: 'info', message: '' });
  const [queryInfo, setQueryInfo] = useState({
    title: "",
    tags: "",
    desc: "",
    isShareable: true,
    queryID: "",
  });
  const [showQueryInfo, setShowQueryInfo] = useState(true);
  const [errors, setErrors] = useState({
    title: {
      show: false,
      message: ""
    },
    database: {
      show: false,
      message: ""
    }
  });
  const [combinedFields, setCombinedFields] = useState([]);
  const [query, setQuery] = useState({
    id: "g-0.8517528779532519",
    rules: [],
    combinator: "and",
    not: false,
  });
  const [advancedMode, setAdvancedMode] = useState(false);
  const [modifiedRules, setModifiedRules] = useState([]);
  const [filterTranslations, setFilterTranslations] = useState([]);
  const [calculateCount, setCalculateCount] = useState(false);
  const [shouldQueryUpdate, setShouldQueryUpdate] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState([]);
  const [filterSteps, setFilterSteps] = useState([]);
  const [initialPatientCount, setInitialPatientCount] = useState(0);
  const [openChildFilters, setOpenChildFilters] = useState(false);
  const [queryResultData, setQueryResultData] = useState({});
  const [patientCount, setPatientCount] = useState({
    total: 0,
    all: 0,
  });
  const [chartData, setChartData] = useState({
    ageAndGender: {},
    adi: {},
    gender: {},
    patientDistribution: {},
  });
  const [listData, setListData] = useState({
    diagnosis: {},
    medications: {},
    procedures: {},
  });
  const [countData, setCountData] = useState({});
  const [partsLoaded, setPartsLoaded] = useState({
    queryResults: true,
    topTenDiagnosis: true,
    topTenMedications: true,
    topTenProcedures: true,
    ageAndGenderChart: true,
    adiChart: true,
    genderChart: true,
    patientDistributionMap: true,
    patientsCount: true,
    proceduresCount: true,
    encountersCount: true,
    labsCount: true,
    medicationsCount: true
  })
  const [openQueryResultModal, setOpenQueryResultModal] = useState(false);
  const [openModal, setOpenModal] = useState({
    filter: false,
    subFilter: false,
    cpt: false,
    icd9: false,
    icd10: false,
    executeQuery: false,
    queryResults: false,
    aiChat: false,
    flow: false
  });
  const [queryObject, setQueryObject] = useState({});
  const [collapsed, setCollapsed] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [leftWidth, setLeftWidth] = useState(window.innerWidth * 0.4);
  const dragStartX = useRef(null);
  const dragStartWidth = useRef(null);
  const [showSql, setShowSql] = useState(false);
  const [sql, setSql] = useState("");
  const [executedSQL, setExecutedSQL] = useState('');
  const [fileName, setFileName] = useState(null);
  const theme = useTheme();
  let navigate = useNavigate();
  const { queryId } = useParams();
  const [initialQuery, setInitialQuery] = useState({});
  const [isSqlDisabled, setIsSqlDisabled] = useState(true);
  const [isExistingQuery, setIsExistingQuery] = useState(false);
  const [hasLoadedQueryInfo, setHasLoadedQueryInfo] = useState(false);
  const [previousFilterHash, setPreviousFilterHash] = useState("");
  const {
    state,
    setState,
    undo,
    redo,
    clear,
    isUndoPossible,
    isRedoPossible,
    pastStates,
    futureStates,
  } = useUndoRedo({
    past: [],
    present: [],
    future: [],
  });
  const [isPatientCountLoading, setIsPatientCountLoading] = useState(false);
  const [patientCountAbortController, setPatientCountAbortController] = useState(null);
  const [tabValue, setTabValue] = useState("queryResults");
  const [copySuccess, setCopySuccess] = useState(false);
  const debounceTimerRef = useRef(null);
  const filterStepsRequestRef = useRef(0);

  const selectedDatabase = useSelector(selectDatabase);
  const metadata = useSelector(selectMetadata);
  const { enableAI } = useSelector((store) => store.settings);
  const selectedAsset = useSelector((store) => store.userAssets.selectedAsset);
  const queryOptions = useSelector(selectQueryOptions);
  const aiFilters = useSelector(selectAIFilters);
  const [filterVersion, setFilterVersion] = useState(0);
  const dispatch = useDispatch();

  // Add location hook to detect route changes
  const location = useLocation();
  const [hasInitialized, setHasInitialized] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  echarts.use([SVGRenderer]);

  // Reset hasInitialized when leaving the QueryBuilder page
  useEffect(() => {
    if (location.pathname !== '/queryBuilder') {
      setHasInitialized(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    setFilterVersion(prev => prev + 1);
  }, [filters]);

  // When Cortex AI sends query_filters, expand them using metadata.queryItems,
  // call setFilters(), and switch to the Query Logic tab.
  // This drives the existing filterSteps → FlowLogic pipeline with real patient counts.
  useEffect(() => {
    if (!aiFilters?.length || !metadata?.queryItems) return;

    const filterMap = {};
    aiFilters.forEach((f) => {
      // Match by key (case-insensitive fallback)
      const queryItem =
        findKeyInObjectsArray(metadata.queryItems, f.key) ||
        findKeyInObjectsArray(metadata.queryItems, f.key?.toUpperCase()) ||
        findKeyInObjectsArray(metadata.queryItems, f.key?.toLowerCase());
      if (!queryItem) return;

      const col = queryItem.column;
      if (!filterMap[col]) {
        filterMap[col] = {
          id: uuidv4(),
          category: queryItem.category,
          friendlyName: queryItem.friendlyName,
          column: queryItem.column,
          key: queryItem.key,
          isChildFilterNumeric: queryItem.isChildFilterNumeric || false,
          combinator: f.combinator || 'or',
          rows: [],
        };
      }
      filterMap[col].rows.push({
        id: uuidv4(),
        field: queryItem.column,
        value: f.value,
        friendlyName: queryItem.friendlyName,
        operator: normalizeOperator(f.operator),
        key: queryItem.key,
        rows: [],
      });
    });

    const expandedFilters = Object.values(filterMap);
    if (expandedFilters.length > 0) {
      setFilters(expandedFilters);
      setTabValue('queryLogic');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiFilters]);

  useEffect(() => {
    return () => {
      setIsExistingQuery(false);
    };
  }, []);

  useEffect(() => {
    if (activeStep === 0 && !isExistingQuery) {
      setShowQueryInfo(true);
      setFilters([]);
      clear();
      setCountData({});
      setExpanded(null);
      clearListAndChartData();
      setQueryInfo({
        title: "",
        tags: "",
        desc: "",
        isShareable: true,
        queryID: "",
      });
      setFileName(null);
      setHasLoadedQueryInfo(false);
    } else if (activeStep === 0) {
      setShowQueryInfo(true);
    } else {
      setShowQueryInfo(false);
    }
  }, [activeStep, isExistingQuery]);

  useEffect(() => {
    if (Object.values(partsLoaded).every(x => x)) {
      setLoading(false);
    }
  }, [partsLoaded])

  useEffect(() => {
    let foundQuery = myQueries.find((x) => x.QueryID === queryId);
    if (foundQuery && !isExistingQuery) {
      setInitialPatientCount(foundQuery.DBPatientCount)
      setIsExistingQuery(true);
      setCountData(prevData => ({
        ...prevData,
        patients: {
          response: foundQuery.MostRecentPatientCount,
          "toolTipLabel": "Total Recorded Patients",
          "type": "count",
          "title": "Patients"
        }
      }));
      return;
    }

    if (filters !== undefined) {
      if (filters.length > 0) {
        if (!isSqlDisabled) {
          setIsSqlDisabled(true);
        }
        const myInit = getQueryBodyObj();
        setCountData({});
        setExpanded(null);
        setTabValue("queryLogic");
        clearListAndChartData();

        // Execute async operations sequentially to avoid race condition
        const executeSequentially = async () => {
          console.log("firing sequential");
          await updateFilterSteps();
          fetchPatientData(myInit);
        };

        executeSequentially();
      } else if (filters.length === 0 && activeStep !== 0) {
        fetchAllData();
        setIsSqlDisabled(true);
        setFilterSteps([]);
      }
    }
  }, [filters]);

  useEffect(() => {
    if (countData.patients && countData.patients.response && filters.length === 0 && activeStep !== 0) {
      setInitialPatientCount(countData.patients.response);
    }
  }, [countData.patients, filters, activeStep]);

  useEffect(() => {
    if (selectedDatabase && metadata && selectedAsset) {
      const distinctCategories = [
        ...new Set(metadata?.queryItems?.map((item) => item.category)),
      ];
      const entitiesArr = [];
      distinctCategories.map((item, index) => {
        const foundEntity = metadata.queryItems.find(
          (entity) => entity.category === item && entity.icon
        ) || metadata.queryItems.find(
          (entity) => entity.category === item
        );
        entitiesArr.push({
          index,
          friendlyName: item,
          icon: foundEntity.icon,
          column: foundEntity.column,
          disabled: foundEntity?.disabled,
          visible: foundEntity?.visible,
          unstructured: foundEntity?.unstructured,
          enableTooltip: foundEntity?.enableTooltip,
          tooltip: foundEntity?.tooltip,
        });
      });

      //sorting entities logic below by splitting into 3 arrays and concatenating them back

      // Arrays to hold separated entities
      const notDisabledUnstructured = [];
      const unstructured = [];
      const disabled = [];
      const notVisible = [];

      // Separate entities into their respective arrays
      entitiesArr.forEach(entity => {
        if (entity.disabled && entity.visible === false) {
          notVisible.push(entity);
        } else if (entity.disabled) {
          disabled.push(entity);
        } else if (entity.unstructured) {
          unstructured.push(entity);
        } else {
          notDisabledUnstructured.push(entity);
        }
      });
      // Sort the notDisabledUnstructured array alphabetically by 'friendlyName'
      notDisabledUnstructured.sort((a, b) => a.friendlyName.localeCompare(b.friendlyName));

      // Concatenate the arrays back into entitiesArr in the specified order
      const sortedEntities = notDisabledUnstructured.concat(unstructured, disabled);
      // Update the state with the sorted entities
      setEntities(sortedEntities);

      if (sortedEntities[0] && metadata) {
        getSimpleFields(metadata, sortedEntities[0], setFields);
      }
    }

    // return () => {
    //   dispatch(clearDatabaseSelection())
    // }
  }, [selectedDatabase, metadata, selectedAsset]);

  useEffect(() => {
    getSharedQueries(userAppSettings.id).then((data) => {
      if (data.success) {
        setSharedQueries(data.data);
      } else {
        setSharedQueries([]);
      }
    });
  }, []);

  useEffect(() => {
    if (advancedMode) {
      GetAllTables().then((res) => {
        if (res && res.data && JSON.parse(res.data).length > 0) {
          const entitiesArray = JSON.parse(res.data);
          //sorting the entities array before setting to state
          entitiesArray.sort((a, b) =>
            a.friendlyName.localeCompare(b.friendlyName)
          );
          setEntities(entitiesArray);
          //this will set the selected entity to the first item in array
          entitiesArray[0].tableName &&
            setSelectedEntity({ index: 0, name: entitiesArray[0].tableName });
        }
      });
    }
  }, [advancedMode]);

  useEffect(() => {
    if (shouldQueryUpdate) {
      updateQuery();
    }
  }, [shouldQueryUpdate]);

  useEffect(() => {
    if (shouldQueryUpdate) {
      setShouldQueryUpdate(false);
      setCalculateCount(true);
    }
  }, [query]);


  // useEffect(() => {
  //   if (JSON.stringify(filters) !== JSON.stringify(state)) {
  //     setState(filters);
  //   }
  // }, [filters, setState, state]);

  useEffect(() => {
    // Prevent circular updates by checking if the state actually differs
    const currentStateString = JSON.stringify(state);
    const filtersString = JSON.stringify(filters);

    if (currentStateString !== filtersString) {
      setState(filters);
    }
  }, [filters, setState, state]);

  useEffect(() => {
    setHasLoadedQueryInfo(false);
  }, [queryId]);

  useEffect(() => {
    if (queryId && queryId !== "" && !hasLoadedQueryInfo) {
      // Check in myQueries
      let foundQuery = null;

      if (myQueries?.length > 0) {
        foundQuery = myQueries.find((x) => x.QueryID === queryId);
      }

      // If not found in myQueries, check in sharedQueries
      if (!foundQuery && sharedQueries?.length > 0) {
        foundQuery = sharedQueries.find((x) => x.QueryID === queryId);
      }

      if (foundQuery) {
        // Set the database and metadata FIRST
        if (foundQuery.DatabaseName) {
          dispatch(setSelectedDatabase(foundQuery.DatabaseName));

        }
        setPatientCount((prevState) => {
          const allCount = foundQuery.MostRecentPatientCount || 0;
          return { ...prevState, total: allCount };
        });

        setIsExistingQuery(true);

        if (foundQuery?.QueryDefinition) {
          try {
            let queryData = JSON.parse(foundQuery.QueryDefinition);

            setQueryInfo({
              title: foundQuery?.QueryName || "",
              tags: Array.isArray(foundQuery?.Tags)
                ? foundQuery.Tags.join(", ")
                : (foundQuery?.Tags || ""),
              desc: foundQuery?.QueryDescription || "",
              definition: foundQuery?.QueryDefinition || "",
              queryID: foundQuery?.QueryID || "",
            });

            setHasLoadedQueryInfo(true);

            if (queryData?.displayFields) {
              queryData.displayFields.sort((a, b) =>
                a.friendlyName?.localeCompare(b.friendlyName)
              );
              setDisplayFields(queryData.displayFields);
            }

            if (queryData?.wheres) {
              setFilters(queryData.wheres);
              setState(queryData.wheres);
            }
          } catch (error) {
            console.error("Error parsing query definition:", error);
            setSnackbarInfo({
              open: true,
              message: "Error loading query: Invalid definition format",
              severity: "error"
            });
          }
        }
      } else if (queryId) {
        // Query ID provided but not found
        setSnackbarInfo({
          open: true,
          message: "Query not found",
          severity: "error"
        });
        navigate('/savedqueries');
      }
    }
  }, [myQueries, sharedQueries, queryId, navigate, hasLoadedQueryInfo]);

  const debounce = (func, delay) => {
    return (...args) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };



  const updateFilterSteps = async () => {
    if (!filters || filters.length === 0) {
      setFilterSteps([]);
      return;
    }

    // Track the latest update cycle
    console.log("firing filterStepsRequest" + filterStepsRequestRef.current)
    filterStepsRequestRef.current += 1;
    const requestId = filterStepsRequestRef.current;

    let filtersSnapshot;
    try {
      filtersSnapshot = JSON.parse(JSON.stringify(filters));
    } catch (error) {
      console.error("Failed to clone filters for flow logic update:", error);
      filtersSnapshot = filters.map(filter => ({ ...filter }));
    }

    if (!filtersSnapshot || filtersSnapshot.length === 0) {
      if (filterStepsRequestRef.current === requestId) {
        setFilterSteps([]);
      }
      return;
    }

    const newFilterSteps = [];

    for (let i = 0; i < filtersSnapshot.length; i++) {
      const currentFilterGroup = filtersSnapshot[i];
      const categoryColor = getCategoryColor(currentFilterGroup.category);

      const currentFilters = filtersSnapshot.slice(0, i + 1);
      const myInit = createQueryBodyObj(
        displayFields,
        queryInfo.title,
        queryInfo.queryID,
        userAppSettings.email,
        currentFilters
      );

      // get patient count for current filters
      const patientCount = await fetchPatientCount(myInit);
      const processedFilters = processFilters(currentFilterGroup.rows);

      newFilterSteps.push({
        category: currentFilterGroup.category,
        patientCount,
        filters: processedFilters,
        combinator: currentFilterGroup.combinator || 'and',
        color: categoryColor
      });
    }

    if (filterStepsRequestRef.current === requestId) {
      // Only commit the update if this is still the latest request
      setFilterSteps(newFilterSteps);
    }
  };

  const processFilters = (rows) => {
    if (!rows) return [];
    return rows.flatMap(row => {
      if (row.id) {
        //a filter object
        return [{
          id: row.id,
          friendlyName: row.friendlyName,
          operator: row.operator,
          value: row.value,
          rows: row.rows ? processFilters(row.rows) : undefined,
        }];
      } else {
        //a field-keyed object with subfilters
        return Object.values(row).flatMap(subfilters => {
          if (Array.isArray(subfilters)) {
            return subfilters.map(subfilter => ({
              id: subfilter.id,
              friendlyName: subfilter.friendlyName,
              operator: subfilter.operator,
              value: subfilter.value,
              rows: subfilter.rows ? processFilters(subfilter.rows) : undefined,
            }));
          } else {
            return [];
          }
        });
      }
    });
  };

  const fetchInitialPatientCountForExistingQuery = async () => {
    if (!isExistingQuery || (initialPatientCount && initialPatientCount !== "0" && initialPatientCount !== 0)) return null;

    try {
      const myInit = {
        body: {
          dbType: metadata?.dbType,
          db: metadata?.db,
          wheres: [],
          email: userAppSettings.email,
        },
      };

      const value = await GetPatientCount(myInit);
      if (value?.results?.response) {
        const count = value.results.response;
        setInitialPatientCount(count);
        return count;
      }
    } catch (error) {
      console.error("Error fetching initial patient count for existing query:", error);
    }
    return null;
  };

  const fetchPatientCount = async (myInit) => {
    try {
      const value = await GetPatientCount(myInit);
      return value.results.response;
    } catch (error) {
      console.error("Error fetching patient count:", error);
      return "Error";
    }
  };

  const getMyQueries = () => {
    getUsersQueries(userAppSettings.id).then((data) => {
      if (data.success) {
        setMyQueries(data.data);
      } else {
        setMyQueries([]);
      }
    });
  };

  const getDownloads = () => {
    checkDownloads().then((data) => {
      const parsedData = JSON.parse(data.data);

      if (data.success && parsedData !== undefined && parsedData !== null) {
        setDownloads(parsedData.contents);
      } else {
        setDownloads([]);
      }
    })
      .catch((err) => {
        console.log({ err })
      })
  };

  const getSimpleFields = (data, selectedEntity, setFields) => {
    const fieldsArr = [];

    // Add null check for data
    if (!data || !data.queryItems) {
      console.warn('Metadata not loaded yet');
      setFields([]);
      return;
    }

    data.queryItems.map((item) => {
      if (item.category === selectedEntity.friendlyName) {
        fieldsArr.push({
          name: item.friendlyName,
          label: item.friendlyName,
          column: item.column,
        });
      }
    });
    fieldsArr.sort((a, b) => a.label.localeCompare(b.label));
    setFields(fieldsArr);
  };

  const getFieldsByTable = (tableName, setterState) => {
    const updatedTableName = advancedMode
      ? selectedEntity.tableName?.split(".")[1]
      : tableName?.split(".")[1];
    GetAllFieldsByTable(updatedTableName).then((res) => {
      if (res) {
        let data = JSON.parse(res.data);
        data.forEach((item) => {
          item.name = item.schemaTableColumnName;
          item.label = item.friendlyName;
        });
        data.sort((a, b) => a.label.localeCompare(b.label));

        if (setterState === setDisplayFields) {
          setDisplayFields([...displayFields, ...data]);
        } else {
          setterState(data);
          setCombinedFields([...combinedFields, ...data]);
        }
      }
    });
  };

  const handleQueryInfoChange = (event, type) => {
    switch (type) {
      case "title":
        return setQueryInfo((prevState) => {
          return { ...prevState, title: event.target.value };
        });
      case "tags":
        return setQueryInfo((prevState) => {
          return { ...prevState, tags: event.target.value };
        });
      case "desc":
        return setQueryInfo((prevState) => {
          return { ...prevState, desc: event.target.value };
        });
      case "isShareable":
        return setQueryInfo((prevState) => {
          return { ...prevState, isShareable: event.target.checked };
        });
      default:
        return;
    }
  };

  const createQueryObj = () => {
    let queryID = undefined;

    if (
      queryObject?.queryID !== undefined &&
      queryObject?.queryID !== null &&
      queryObject?.queryID.length > 0
    ) {
      queryID = queryObject.queryID;
    } else if (
      queryInfo?.queryID !== undefined &&
      queryInfo?.queryID !== null &&
      queryInfo?.queryID.length !== 0
    ) {
      queryID = queryInfo.queryID;
    }

    const q = {
      queryID: queryID ?? userAppSettings.id + new Date().toISOString(),
      name: queryInfo.title,
      description: queryInfo.desc,
      tags: queryInfo.tags,

      queryData: JSON.stringify({ displayFields, wheres: filters }),
      isShareable: queryInfo.isShareable,
    };

    setQueryObject(q);

    return q;
  };

  const createUserSettingsObj = () => {
    const userData = {
      id: userAppSettings.id,
      email: userAppSettings.email,
      approvalStatus: userAppSettings.approvalStatus, // ,
    };
    return userData;
  };

  const handleSaveQuery = async (executedSQL, mostRecentPatientCount, dbPatientCount) => {
    if (queryInfo.title != "" && filters.length > 0) {
      try {
        const myInit = createSaveQueryBodyObj(executedSQL.replaceAll('\\', '\\\\').replace('"', '\\"'), mostRecentPatientCount, dbPatientCount);
        const result = await API.post(
          "CRCRestAPI",
          "/api/v1/saveQuery",
          myInit
        );

        if (result.success) {
          setFileName(result?.fileName);
        }
        setUserAppSettings(createUserSettingsObj());

        setSnackbarInfo({
          open: true,
          message: "Query Saved!",
          severity: "success",
        });
      } catch (ex) {
        console.error(ex);
        return setSnackbarInfo({
          open: true,
          message: "Error Saving Query!",
          severity: "error",
        });
      }
    } else {
      /*
      return setSnackbarInfo({
        open: true,
        message: "Cannot save empty query",
        severity: "error",
      });
      */
    }
  };

  const updateQuery = () => {
    if (modifiedRules && modifiedRules.length > 0) {
      const arr = [];
      modifiedRules.map((rule) => {
        if (!rule.any) {
          const foundRule = arr.find(
            (r) => r.field === evaluateField(rule.field)
          );
          if (!foundRule) {
            arr.push({
              ...rule,
              field: evaluateField(rule.field),
              dataType: evaluateField(rule.dataType),
              selectOptions: [],
            });
          }
        }
      });
      if (query && Object.prototype.hasOwnProperty.call(query, "rules")) {
        setQuery((prevState) => {
          return { ...prevState, rules: arr };
        });
      } else {
        setQuery({
          id: "g-0.8517528779532519",
          rules: arr,
          combinator: "and",
          not: false,
        });
      }
    }
  };

  const createSaveQueryBodyObj = (executedSQL, mostRecentPatientCount, dbPatientCount = null) => {
    const queryObject = createQueryObj();
    const userSettings = createUserSettingsObj();

    const myInit = {
      body: {
        queryID: queryObject?.queryID,
        userID: userSettings?.id,
        queryName: queryObject?.name,
        queryDescription: queryObject?.description,
        queryDefinition: queryObject?.queryData,
        tags: queryObject?.tags,
        isShareable: queryObject?.isShareable,
        email: userSettings?.email,
        db: metadata.db,
        isNew: true,
        executedSQL,
        mostRecentPatientCount: mostRecentPatientCount ?? 0,
        dbPatientCount: dbPatientCount || initialPatientCount || null
      },
    };

    return myInit;
  };

  const getQueryBodyObj = () => {
    return createQueryBodyObj(
      displayFields,
      queryInfo.title,
      queryInfo.queryID,
      userAppSettings.email
    );
  }

  const createQueryBodyObj = (displayFields, queryName, queryID, email, filtersOverride = null) => {
    let createdQuery;
    if (filtersOverride) {
      createdQuery = createRules(filtersOverride);
    } else {
      createdQuery = createRules(filters);
    }

    const patientKey = metadata.patientKey;

    // starting with default data center key for fields to display
    let fieldsArr = [
      {
        name: "Data Center Key",
        field: patientKey,
        label: "Data Center Key",
      },
    ];

    if (displayFields === undefined) {
      displayFields = [];
    }

    setDisplayFields([...displayFields, ...fieldsArr]);
    const currentDisplayFields = [...displayFields, ...fieldsArr];
    const uniqueDisplayFields = [
      ...new Map(
        currentDisplayFields.map((v) => [JSON.stringify(v), v])
      ).values(),
    ];

    const myInit = {
      body: {
        dbType: selectedDatabase ? selectedDatabase.dbType : null,
        db: metadata ? metadata.db : null,
        modifiers: [
          {
            modifier: "TOP 100",
          },
        ],
        fields: [],
        wheres: {
          id: "g-0.8517528779532519",
          rules: createdQuery,
          combinator: "and",
          not: false,
        },
        orderBys: sorting,
        queryName: queryName,
        queryID: queryID,
        email: email,
        isNew: true,
      },
    };

    if (filters?.length > 0) {
      setQuery(myInit.body.wheres);
    }
    return myInit;
  };



  const fetchPatientData = useCallback(async (myInit) => {
    if (patientCountAbortController) {
      patientCountAbortController.abort();
    }

    const newAbortController = new AbortController();
    setPatientCountAbortController(newAbortController);
    setIsPatientCountLoading(true);
    try {
      setPartsLoaded(prevData => ({ ...prevData, patientsCount: false }));
      const value = await GetPatientCount(myInit, newAbortController.signal);
      if (value) {
        setCountData(prevData => ({
          ...prevData,
          patients: value.results,
        }
        ));
      }
    } catch (exception) {
      if (exception.name === 'AbortError') {
        console.log('Fetch aborted');
      } else {
        console.error({ exception });
        setCountData(prevData => ({
          ...prevData,
          patients: { response: "Retry" }
        }));
      }
    } finally {
      setIsPatientCountLoading(false);
      setPatientCountAbortController(null);
      setPartsLoaded(prevData => ({
        ...prevData,
        patientsCount: true
      }));
    }
  }, []);

  const debouncedFetchPatientData = useCallback(
    debounce((myInit) => {
      fetchPatientData(myInit);
    }, 1000),
    [fetchPatientData]
  );

  useEffect(() => {
    if (filters !== undefined && filters.length > 0) {
      const myInit = getQueryBodyObj();
      debouncedFetchPatientData(myInit);
    } else if (filters?.length === 0 && activeStep !== 0) {
      fetchAllData();
    }

    return () => {
      if (patientCountAbortController) {
        patientCountAbortController.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [filters, debouncedFetchPatientData]);

  const fetchEncounterData = async (myInit) => {
    GetEncounterCount(myInit).then((value) => {
      setCountData(prevData => ({
        ...prevData,
        encounters: value.results,
      }));
    }).catch((exception) => {
      console.error({ exception });
      setCountData(prevData => ({
        ...prevData,
        encounters: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        encountersCount: true,
      }));
    });
  }

  const fetchMedicationData = async (myInit) => {
    GetMedicationCount(myInit).then((value) => {
      setCountData(prevData => ({
        ...prevData,
        medications: value.results,
      }));
    }).catch((exception) => {
      console.error({ exception });
      setCountData(prevData => ({
        ...prevData,
        medications: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        medicationsCount: true,
      }));
    });
  }

  const fetchProcedureData = async (myInit) => {
    GetProcedureCount(myInit).then((value) => {
      setCountData(prevData => ({
        ...prevData,
        procedures: value.results,
      }));
    }).catch((exception) => {
      console.error({ exception });
      setCountData(prevData => ({
        ...prevData,
        procedures: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        proceduresCount: true,
      }));
    });
  }

  const fetchLabData = async (myInit) => {
    GetLabCount(myInit).then((value) => {
      setCountData(prevData => ({
        ...prevData,
        labs: value.results,
      }));
    }).catch((exception) => {
      console.error({ exception });
      setCountData(prevData => ({
        ...prevData,
        labs: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        labsCount: true,
      }));
    });
  }

  const fetchTopTenDiagnosisData = async (myInit) => {
    GetTopTenDiagnoses(myInit).then((value) => {
      setListData(prevData => ({
        ...prevData,
        diagnosis: value.results,
      }));
    }).catch((exception) => {
      console.error({ exception });
      setListData(prevData => ({
        ...prevData,
        diagnosis: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        topTenDiagnosis: true,
      }));
    });
  }

  const fetchTopTenMedicationsData = async (myInit) => {
    GetTopTenMedications(myInit).then((value) => {
      setListData(prevData => ({
        ...prevData,
        medications: value.results,
      }));
    }).catch((exception) => {
      console.error({ exception });
      setListData(prevData => ({
        ...prevData,
        medications: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        topTenMedications: true,
      }));
    });
  }

  const fetchTopTenProceduresData = async (myInit) => {
    GetTopTenProcedures(myInit).then((value) => {
      setListData(prevData => ({
        ...prevData,
        procedures: value.results,
      }));
    }).catch((exception) => {
      console.error({ exception });
      setListData(prevData => ({
        ...prevData,
        procedures: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        topTenProcedures: true,
      }));
    });
  }

  const fetchAgeDistributionData = async (myInit) => {
    GetAgeDistribution(myInit).then((value) => {
      setChartData(prevData => ({
        ...prevData,
        ageAndGender: value.results,
      }));
    }).catch((exception) => {
      console.error({ exception });
      setChartData(prevData => ({
        ...prevData,
        ageAndGender: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        ageAndGenderChart: true,
      }));
    });
  }

  const fetchAdiScoreDistributionData = async (myInit) => {
    GetAdiScoreDistribution(myInit).then((value) => {
      setChartData(prevData => ({
        ...prevData,
        adi: value.results,
      }));
    }).catch((exception) => {
      console.error({ exception });
      setChartData(prevData => ({
        ...prevData,
        adi: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        adiChart: true,
      }));
    });
  }

  const fetchGenderDistributionData = async (myInit) => {
    GetGenderDistribution(myInit).then((value) => {
      setChartData(prevData => ({
        ...prevData,
        gender: value.results,
      }));
    }).catch((exception) => {
      console.error({ exception });
      setChartData(prevData => ({
        ...prevData,
        gender: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        genderChart: true,
      }));
    });

  }

  const fetchPatientHeatMapByStateData = async (myInit) => {
    GetPatientHeatmapByState(myInit).then((value) => {
      if (value.success) {
        setChartData(prevData => ({
          ...prevData,
          patientDistribution: value.results,
        }));

        const { usaJSON, specialAreas } = value.results;

        //specialAreas are the three states, alaska, puerto rico, and hawaii and their placement
        //usaJSON is the data needed by echarts since it no longer has these part of the library
        echarts.registerMap("USA", usaJSON, specialAreas);
      }
    }).catch((exception) => {
      console.error({ exception });
      setChartData(prevData => ({
        ...prevData,
        patientDistribution: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        patientDistributionMap: true,
      }));
    });
  }

  const fetchAllData = () => {
    const myInit = {
      body: {
        dbType: metadata.dbType,
        db: metadata.db,
        tableName: metadata.patientTableName,
        email: userAppSettings.email,
      },
    };
    // Always fetch patient data as it's required
    fetchPatientData(myInit);
    fetchCountsAndChartsData(myInit, null);
  }

  const clearListAndChartData = () => {
    setListData({
      diagnosis: {},
      medications: {},
      procedures: {},
    });

    setChartData({
      ageAndGender: {},
      adi: {},
      gender: {},
      patientDistribution: {},
    });

    setQueryResultData({});

    setCountData((prevData) => {
      const remaining = { ...prevData };
      OPTIONAL_COUNT_KEYS.forEach((key) => delete remaining[key]);
      return remaining;
    });
  }

  // complete reset function to ensure consistent state clearing
  const performCompleteReset = useCallback(() => {
    setActiveStep(0);
    setShowQueryInfo(true);
    setSelectedEntity({});
    setFilters([]);
    setFilterSteps([]);
    clear();
    setCountData({});
    setExpanded(null);
    clearListAndChartData();
    setQueryInfo({
      title: "",
      tags: "",
      desc: "",
      isShareable: true,
      queryID: "",
    });
    setIsExistingQuery(false);
    setHasLoadedQueryInfo(false);
    setFileName(null);

    setTimeout(() => {
      dispatch(clearDatabaseSelection());
      dispatch(setSelectedAsset(null));
    }, 0);

    // Force QueryInfo to remount by changing its key
    setResetKey(prev => prev + 1);
  }, [setActiveStep, clear, dispatch]);

  // Reset query state when navigating to QueryBuilder (unless loading existing query)
  useEffect(() => {
    if (location.pathname === '/queryBuilder' && !queryId && !hasInitialized) {
      performCompleteReset();
      setHasInitialized(true);
    }
  }, [location.pathname, queryId, hasInitialized, performCompleteReset]);

  // Additional reset when activeStep is set to 0 from sidebar
  useEffect(() => {
    if (activeStep === 0 && !queryId && location.pathname === '/queryBuilder' && hasInitialized) {
      performCompleteReset();
    }
  }, [activeStep, queryId, location.pathname, hasInitialized, performCompleteReset]);

  //rest of the count & data fetches
  const fetchCountsAndChartsData = (myInit, dbPatientCount = null, isUserInitiated = false) => {
    clearListAndChartData();

    // fileName is the S3 key, so clear it up front - a stale one lets a
    // failed run download the previous query's zip
    setFileName(null);

    const shouldFetchAllData = filters.length === 0 && !isExistingQuery;
    const willFetchOptionalData = !isExistingQuery || isUserInitiated;

    // Set ALL loading flags upfront to prevent race condition
    setPartsLoaded(prev => ({
      ...prev,
      queryResults: false,
      ...(willFetchOptionalData && {
        encountersCount: (shouldFetchAllData || queryOptions.encounterCount) ? false : true,
        medicationsCount: (shouldFetchAllData || queryOptions.medicationCount) ? false : true,
        proceduresCount: (shouldFetchAllData || queryOptions.procedureCount) ? false : true,
        labsCount: (shouldFetchAllData || queryOptions.labCount) ? false : true,
        topTenDiagnosis: (shouldFetchAllData || queryOptions.top10Lists) ? false : true,
        topTenMedications: (shouldFetchAllData || queryOptions.top10Lists) ? false : true,
        topTenProcedures: (shouldFetchAllData || queryOptions.top10Lists) ? false : true,
        ageAndGenderChart: (shouldFetchAllData || queryOptions.distributionCharts) ? false : true,
        adiChart: (shouldFetchAllData || queryOptions.distributionCharts) ? false : true,
        genderChart: (shouldFetchAllData || queryOptions.distributionCharts) ? false : true,
        patientDistributionMap: (shouldFetchAllData || queryOptions.distributionMap) ? false : true,
      }),
    }));

    // Always fetch the essential patient data
    QueryPatientData(myInit).then(async (value) => {
      const patientDataResult = value.results;
      setSql(patientDataResult.patientDataQuery);
      setQueryResultData(patientDataResult);

      const patientDataQuery = patientDataResult.patientDataQuery;
      const queryWithoutLimits = patientDataQuery.substring(0, patientDataQuery.indexOf("LIMIT") - 1);
      const mostRecentPatientCount = Number(patientDataResult.patientCount.replaceAll(',', ''));

      const currentDbPatientCount = dbPatientCount || initialPatientCount;

      // Generate fileName for download functionality
      const generateFileName = () => {
        const timestamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0];
        const baseName = queryInfo.title || 'query_results';
        return `${baseName}_${timestamp}.csv`;
      };

      // Only set fileName when user has built a query with filters (not for cached results)
      // This ensures Download button is only enabled after user runs a query
      if (filters.length > 0) {
        // Always regenerate - never reuse a fileName across queries
        const generatedFileName = generateFileName();
        setFileName(generatedFileName);

        // Try to save the query (this will only save if title exists and filters exist)
        // If successful, the API may return a different fileName which will be set by handleSaveQuery
        await handleSaveQuery(queryWithoutLimits, mostRecentPatientCount, currentDbPatientCount);
      }

      setExecutedSQL(patientDataQuery);

      // Only expand chartData accordion if distribution charts are selected or no filters (show all)
      const shouldExpandChartData = filters.length === 0 || queryOptions.distributionCharts;
      if (shouldExpandChartData) {
        setExpanded('chartData');
      }
    })
      .catch((exception) => {
        console.log({ exception });
      })
      .finally(() => {
        setPartsLoaded(prev => ({ ...prev, queryResults: true }));
      });

    if (isExistingQuery && !isUserInitiated) {
      return; // Don't auto-fetch expensive data when saved query first loads
    }

    // When no filters: always fetch everything (cached results - ignore localStorage)
    // When has filters: respect user's localStorage selections
    if (shouldFetchAllData) {
      // Fetch all data when no filters (cached results)
      fetchEncounterData(myInit);
      fetchMedicationData(myInit);
      fetchProcedureData(myInit);
      fetchLabData(myInit);
      fetchTopTenDiagnosisData(myInit);
      fetchTopTenMedicationsData(myInit);
      fetchTopTenProceduresData(myInit);
      fetchAgeDistributionData(myInit);
      fetchAdiScoreDistributionData(myInit);
      fetchGenderDistributionData(myInit);
      fetchPatientHeatMapByStateData(myInit);
    } else {
      // Fetch data based on user's selections when filters exist
      if (queryOptions.encounterCount) {
        fetchEncounterData(myInit);
      }
      if (queryOptions.medicationCount) {
        fetchMedicationData(myInit);
      }
      if (queryOptions.procedureCount) {
        fetchProcedureData(myInit);
      }
      if (queryOptions.labCount) {
        fetchLabData(myInit);
      }
      if (queryOptions.top10Lists) {
        fetchTopTenDiagnosisData(myInit);
        fetchTopTenMedicationsData(myInit);
        fetchTopTenProceduresData(myInit);
      }
      if (queryOptions.distributionCharts) {
        fetchAgeDistributionData(myInit);
        fetchAdiScoreDistributionData(myInit);
        fetchGenderDistributionData(myInit);
      }
      if (queryOptions.distributionMap) {
        fetchPatientHeatMapByStateData(myInit);
      }
    }
  }

  const fetchFunctions = {
    fetchPatientData: (myInit) => fetchPatientData(myInit),
    fetchEncounterData: (myInit) => fetchEncounterData(myInit),
    fetchMedicationData: (myInit) => fetchMedicationData(myInit),
    fetchProcedureData: (myInit) => fetchProcedureData(myInit),
    fetchLabData: (myInit) => fetchLabData(myInit),
    fetchTopTenDiagnosisData: (myInit) => fetchTopTenDiagnosisData(myInit),
    fetchTopTenMedicationsData: (myInit) => fetchTopTenMedicationsData(myInit),
    fetchTopTenProceduresData: (myInit) => fetchTopTenProceduresData(myInit),
    fetchAgeDistributionData: (myInit) => fetchAgeDistributionData(myInit),
    fetchAdiScoreDistributionData: (myInit) => fetchAdiScoreDistributionData(myInit),
    fetchGenderDistributionData: (myInit) => fetchGenderDistributionData(myInit),
    fetchPatientHeatMapByStateData: (myInit) => fetchPatientHeatMapByStateData(myInit),
  };

  const handleExecuteQuery = async () => {
    setLoading(true);
    setTabValue("queryResults");

    // Fetch initial patient count for existing queries if needed
    let fetchedInitialPatientCount = null;
    if (isExistingQuery && (!initialPatientCount || initialPatientCount === "0" || initialPatientCount === 0)) {
      fetchedInitialPatientCount = await fetchInitialPatientCountForExistingQuery();
    }

    // Update filter steps with sequential count fetching
    console.log("firing seq");
    await updateFilterSteps();

    // Fetch other data - this will now respect query options for filtered queries
    const myInit = getQueryBodyObj();
    setInitialQuery(myInit);

    // Clear the isExistingQuery flag when user actively runs a query
    if (isExistingQuery) {
      setIsExistingQuery(false);
    }

    // Pass true to indicate this is user-initiated, not automatic
    fetchCountsAndChartsData(myInit, fetchedInitialPatientCount || initialPatientCount, true);
    setIsSqlDisabled(false);
  };

  const handleNext = () => {
    let hasErrors = false;
    const newErrors = {
      title: { show: false, message: "" },
      database: { show: false, message: "" }
    };

    if (!selectedDatabase) {
      newErrors.database.show = true;
      newErrors.database.message = "Please select a database before proceeding.";
      hasErrors = true;
    }

    const foundQuery = myQueries?.find((q) => q.QueryName === queryInfo.title);
    if (foundQuery && showQueryInfo && foundQuery.QueryID !== queryInfo.queryID) {
      newErrors.title.show = true;
      newErrors.title.message = "Title already taken. Please enter unique title.";
      hasErrors = true;
    } else if (queryInfo.title === "" && showQueryInfo) {
      newErrors.title.show = true;
      newErrors.title.message = "Please add title before moving to next step.";
      hasErrors = true;
    }

    setErrors(newErrors);

    if (hasErrors) {
      return;
    }

    let allCount = 0;

    if (foundQuery !== undefined) {
      allCount = foundQuery.MostRecentPatientCount;
    }

    if (queryInfo.queryId && queryInfo.queryId != "") {
      if (myQueries?.length > 0) {
        setQueryInfo({
          queryID: foundQuery?.queryID,
          title: foundQuery?.name,
          tags: foundQuery?.tags,
          desc: foundQuery?.description,
        });

        if (foundQuery) {
          let queryData = JSON.parse(foundQuery?.QueryDefinition);
          queryData?.displayFields?.sort((a, b) =>
            a.friendlyName?.localeCompare(b.friendlyName)
          );

          setDisplayFields(queryData.displayFields);
          setFilters(queryData.wheres);
          setState(queryData.wheres);
        }
      }
    }
    setActiveStep(1);
    setShowQueryInfo(false);

    getMyQueries();
    //not sure why we are calling getDownloads call here, so commented it out
    //getDownloads();

    setLoading(true);
    //call to get initial results of patient summary
    if (!filters || filters.length === 0) {
      fetchAllData();
    } else {
      const myInit = getQueryBodyObj();
      fetchCountsAndChartsData(myInit, null);
    }
  };

  const handleBack = () => {
    setShowQueryInfo(true);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const findKeyInObjectsArray = (objectsArray, keyToFind) => {
    for (let i = 0; i < objectsArray.length; i++) {
      const currentObject = objectsArray[i];
      if (
        currentObject.key === keyToFind ||
        currentObject.column === keyToFind
      ) {
        return currentObject;
      }
      if (currentObject.childFilters) {
        const result = findKeyInObjectsArray(
          currentObject.childFilters,
          keyToFind
        );
        if (result) {
          return result;
        }
      }
    }
    return null;
  };

  const handleReset = () => {
    setDisplayFields([]);
    setFilterTranslations([]);

    setPatientCount((prevState) => {
      return { ...prevState, total: patientCount.all };
    });

    setFilters([]);
  };

  const handleCloseQueryResultsModal = () => {
    setOpenQueryResultModal(false);
  };

  const handleUndo = () => {
    if (isUndoPossible) {
      const previousState = undo();
      if (previousState && JSON.stringify(previousState) !== JSON.stringify(filters)) {
        setFilters(previousState);
      }
    }
  };

  const handleRedo = () => {
    if (isRedoPossible) {
      const nextState = redo();
      if (nextState && JSON.stringify(nextState) !== JSON.stringify(filters)) {
        setFilters(nextState);
      }
    }
  };

  const handleOpenAIModal = () => {
    setOpenModal({ ...openModal, aiChat: true });
  };

  const handleCloseAIModal = () => {
    setOpenModal({ ...openModal, aiChat: false });
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleMouseDown = (e) => {
    dragStartX.current = e.clientX;
    dragStartWidth.current = leftWidth;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (dragStartX.current !== null) {
      const deltaX = e.clientX - dragStartX.current;
      setLeftWidth(dragStartWidth.current + deltaX);
    }
  };

  const handleMouseUp = () => {
    dragStartX.current = null;
    dragStartWidth.current = null;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const handlePanelClose = () => {
    if (collapsed) {
      setLeftWidth(window.innerWidth * 0.4);
    } else {
      setLeftWidth(1);
    }
    setCollapsed(!collapsed);
  };

  if (showQueryInfo) {
    return (
      <Box display={"flex"} justifyContent={"center"}>
        <QueryInfo
          key={resetKey}
          handleQueryInfoChange={handleQueryInfoChange}
          queryInfo={queryInfo}
          errors={errors}
          headerBackgroundColor={"#04aeea"}
          headerColor={"white"}
          handleNext={handleNext}
          userId={userAppSettings?.id}
          isEditMode={isExistingQuery}
        />
      </Box>
    );
  }

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarInfo({ ...snackbarInfo, open: false });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCopySQL = () => {
    navigator.clipboard.writeText(sql)
      .then(() => {
        setCopySuccess(true);
        setSnackbarInfo({
          open: true,
          message: "Copied SQL to clipboard",
          severity: "success",
        });
        setTimeout(() => setCopySuccess(false), 3000);
      })
      .catch(err => {
        console.error('Failed to copy SQL query: ', err);
        setSnackbarInfo({
          open: true,
          message: "Failed to copy SQL to clipboard",
          severity: "error",
        });
      });
  };

  echarts.registerTheme("walden", walden);
  return (
    <Box
      display={"flex"}
      justifyContent={"center"}
      sx={{
        backgroundColor: 'surface.page',
        minHeight: '100vh',
        p: 2
      }}
    >
      <Grid container sx={{ width: "100%" }}>
        <Grid size={12}>
          <TopToolbar
            handleExecuteQuery={handleExecuteQuery}
            isSqlDisabled={isSqlDisabled}
            showSql={showSql}
            setShowSql={setShowSql}
            isUndoPossible={isUndoPossible}
            handleUndo={handleUndo}
            isRedoPossible={isRedoPossible}
            handleRedo={handleRedo}
            handleReset={handleReset}
            handleCopySQL={handleCopySQL}
            metadata={metadata}
            filters={filters}
            sql={sql}
            entities={entities}
            fileName={fileName}
            db={metadata?.db}
            setSnackbarInfo={setSnackbarInfo}
            queryResultData={queryResultData}
            loading={loading}
          />
        </Grid>
        <Grid
          size={12}
          sx={{
            display: "flex",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              gap: 2,
            }}
          >
            <Paper
              elevation={1}
              sx={{
                flex: .7,
                minWidth: 0,
                p: 1,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                maxHeight: "91vh",
                overflow: "auto",
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <Folder size={20} color="currentColor" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Data Categories
                </Typography>
              </Box>
              <EntitiesBar
                advancedMode={advancedMode}
                entities={entities}
                selectedEntity={selectedEntity}
                setSelectedEntity={setSelectedEntity}
                setFields={setFields}
                getFieldsByTable={getFieldsByTable}
                getSimpleFields={getSimpleFields}
                openChildFilters={openChildFilters}
                setOpenChildFilters={setOpenChildFilters}
                filterModalOpen={filterModalOpen}
                setFilterModalOpen={setFilterModalOpen}
              />
            </Paper>

            <Paper
              elevation={1}
              sx={{
                flex: 1,
                minWidth: 0,
                p: 1,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                display: "flex",
                flexDirection: "column",
                maxHeight: "91vh",
                overflow: "auto",
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <Wrench size={20} color="currentColor" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Query Builder
                </Typography>
              </Box>

              <Box sx={{
                flex: "1 1 auto",
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}>
                <SimpleFilters
                  key={resetKey}
                  selectedTable={selectedEntity}
                  modifiedRules={modifiedRules}
                  setModifiedRules={setModifiedRules}
                  filterTranslations={filterTranslations}
                  setFilterTranslations={setFilterTranslations}
                  setCalculateCount={setCalculateCount}
                  setQuery={setQuery}
                  setShouldQueryUpdate={setShouldQueryUpdate}
                  filters={state}
                  setFilters={setFilters}
                  showSql={showSql}
                  //passing in this setState below to clear filters
                  setState={setState}
                  pastFilters={pastStates}
                  openChildFilters={openChildFilters}
                  setOpenChildFilters={setOpenChildFilters}
                  filterModalOpen={filterModalOpen}
                  setFilterModalOpen={setFilterModalOpen}
                  metadata={metadata}
                  setSnackbarInfo={setSnackbarInfo}
                  loading={loading}
                  setLoading={setLoading}
                />
                <Box sx={{ flexGrow: 1 }} />
                {filters.length > 0 && showSql
                  &&
                  <Box sx={{
                    borderTop: "1px solid",
                    borderTopColor: 'divider',
                    padding: '16px',
                    flexShrink: 0,
                    marginTop: 'auto'
                  }}>
                    <Grid container size={12} sx={{
                      borderBottom: '1px solid',
                      borderBottomColor: 'divider',
                      paddingBottom: '12px',
                      marginBottom: '16px'
                    }}>
                      <Grid size={8} sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <DataObjectIcon sx={{ color: 'text.secondary', fontSize: '20px' }} />
                        <Typography sx={{
                          color: 'text.primary',
                          fontWeight: 600,
                          fontSize: '1rem'
                        }}>
                          Query Preview
                        </Typography>
                      </Grid>
                      <Grid size={4} sx={{
                        textAlign: 'right',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 1
                      }}>
                        <Tooltip title="Copy SQL">
                          <ContentCopyIcon
                            sx={{
                              cursor: 'pointer',
                              color: 'text.secondary',
                              fontSize: '18px',
                              '&:hover': {
                                color: 'text.primary',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                            onClick={handleCopySQL}
                          />
                        </Tooltip>
                        <CloseIcon
                          sx={{
                            cursor: 'pointer',
                            color: 'text.secondary',
                            fontSize: '18px',
                            '&:hover': {
                              color: 'text.primary',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                          onClick={() => setShowSql(false)}
                        />
                      </Grid>
                    </Grid>
                    <Box sx={{
                      backgroundColor: '#1e293b',
                      borderRadius: '12px',
                      padding: '20px',
                      maxHeight: '180px',
                      overflow: 'auto',
                      border: '1px solid #334155',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#0f172a',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#475569',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: '#64748b',
                      }
                    }}>
                      <Typography sx={{
                        overflowWrap: "break-word",
                        textWrap: 'wrap',
                        color: "#10b981",
                        fontSize: ".95rem",
                        fontFamily: '"Fira Code", "Monaco", "Cascadia Code", "Roboto Mono", monospace',
                        lineHeight: 1.6,
                        letterSpacing: '0.02em',
                        fontWeight: 400
                      }}>
                        {sql}
                      </Typography>
                    </Box>

                  </Box>
                }
              </Box>
            </Paper>

            <Paper
              elevation={1}
              sx={{
                flex: 2,
                minWidth: 0,
                p: 1,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                maxHeight: "91vh",
                overflow: "auto",
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <BarChart3 size={20} color="currentColor" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Data Visualization
                </Typography>
              </Box>
              <TabContext value={tabValue}>
                <TabList onChange={handleTabChange}>
                  <Tab label="Query Results" value="queryResults" />
                  <Tab label="Query Logic" value="queryLogic" />
                </TabList>
                <TabPanel value="queryResults">
                  <Grid container spacing={1} justifyContent={"space-between"}>
                    <Grid size={12}>
                      <Counts
                        countData={countData}
                        partsLoaded={partsLoaded}
                        fetchFunctions={fetchFunctions}
                        initialQuery={initialQuery}
                        queryOptions={queryOptions}
                      />
                    </Grid>
                    <Grid size={12}>
                      <ListDataAccordion
                        expanded={expanded}
                        handleAccordionChange={handleAccordionChange}
                        listData={listData}
                        fetchFunctions={fetchFunctions}
                        initialQuery={initialQuery}
                        partsLoaded={partsLoaded}
                      />
                      <ChartDataAccordion
                        expanded={expanded}
                        handleAccordionChange={handleAccordionChange}
                        chartData={chartData}
                        fetchFunctions={fetchFunctions}
                        initialQuery={initialQuery}
                        partsLoaded={partsLoaded}
                      />
                      <Accordion
                        expanded={expanded === "heatMap"}
                        onChange={handleAccordionChange("heatMap")}
                        disabled={Object.keys(chartData?.patientDistribution).length === 0}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="panel1a-content"
                          className="accordion-summary"
                        >
                          <Typography
                            variant="h6"
                            className="accordian-title"
                            fontSize={"1rem"}
                          >
                            Patient Distribution Map
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Paper
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              backgroundColor: "surface.raised",
                              boxShadow: (t) => t.palette.appShadow.card,
                            }}
                          >
                            <Box padding={2}>
                              <Typography
                                sx={{
                                  fontSize: "0.87rem",
                                  color: "text.secondary",
                                  fontWeight: 400,
                                }}
                              >
                                Patient Distribution Map
                              </Typography>
                              {!loading && chartData?.patientDistribution?.response
                                ? (typeof chartData?.patientDistribution?.response === 'object'
                                  ? <ReactECharts
                                    option={applyEChartsTheme(chartData?.patientDistribution?.response, theme)}
                                    opts={{ renderer: "svg" }}
                                    theme={"walden"}
                                  />
                                  : <Button color="error" onClick={() => fetchFunctions.fetchPatientHeatMapByStateData()}>Retry</Button>
                                )
                                : <Button color="error" onClick={() => fetchFunctions.fetchPatientHeatMapByStateData()}>Retry</Button>
                              }
                            </Box>
                          </Paper>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                    <Grid size={12}>
                      <QueryResults
                        resultData={queryResultData}
                        fetchFunctions={fetchFunctions}
                        initialQuery={initialQuery}
                        sql={sql}
                        fileName={fileName}
                        isLoading={loading}
                        db={metadata.db}
                        //db={selectedAsset.AssetName}
                        entities={entities}
                        isSqlDisabled={isSqlDisabled}
                        setSnackbarInfo={setSnackbarInfo}
                      />
                    </Grid>
                  </Grid>

                </TabPanel>
                <TabPanel value="queryLogic">
                  <FlowLogic
                    filterSteps={filterSteps}
                    metadata={metadata}
                    initialPatientCount={initialPatientCount}
                    isPatientCountLoading={isPatientCountLoading}
                    filterVersion={filterVersion}
                  />
                </TabPanel>
              </TabContext>
            </Paper>
          </Box>
        </Grid>
      </Grid>
      <AskAIModal
        openModal={openModal.aiChat}
        handleCloseModal={handleCloseAIModal}
        setSnackbarInfo={setSnackbarInfo}
        setFilters={setFilters}
        metadata={metadata}
      />
      <AnalysisModal queryResultData={queryResultData} />
      <CustomSnackbar
        severity={snackbarInfo.severity}
        message={snackbarInfo.message}
        open={snackbarInfo.open}
        handleClose={handleClose}
      />
    </Box>
  );
};

export default QueryBuilder;
